'use strict'

const inRange_ = require('lodash/inRange')

const { matchAction } = require('redux-serial-effects')

const defaultDatasetConfiguration = require('dbsm-common/src/dataset-configuration/defaults')
const actionTypes = require('./actionTypes')
const { saveStates, isNone, isError } = require('./saveStates')
const rootActions = require('../dataset-controller/actions')

const initialRecordsState = {
  currentRecord: {
    index: undefined,
    data: undefined,
    fieldsToUpdate: undefined,
    updateSource: undefined
  },
  lastSavedRecord: {
    index: undefined,
    data: undefined
  },
  desiredIndex: undefined,
  refreshCurrentRecord: false,
  removeCurrentRecord: false,
  newRecordIndex: null,
  refreshCurrentView: false,
  revertChanges: false,
  saveState: saveStates.SAVE_STATE_NONE,
  defaultRecord: undefined,
  page: {
    numPagesToShow: 1,
    offset: 0,
    size: defaultDatasetConfiguration.pageSize
  },
  schema: undefined
}
const getDefaultDraft = state => Object.assign({}, state.defaultRecord)

const isIndexInView = (index, state) =>
  inRange_(
    index,
    getPaginationData(state).offset,
    getPaginationData(state).offset + getPaginationData(state).size
  )

const isCurrentIndexInView = state =>
  isIndexInView(selectCurrentRecordIndex(state), state)

const setCurrentRecord = (state, index, record) => {
  const newState = Object.assign({}, state, {
    currentRecord: { index, data: record },
    desiredIndex: index,
    refreshCurrentRecord: false
  })

  return isCurrentIndexInView(newState)
    ? newState
    : Object.assign({}, newState, {
        page: Object.assign({}, newState.page, {
          offset:
            Math.floor(newState.currentRecord.index / newState.page.size) *
            newState.page.size,
          numPagesToShow: 1
        })
      })
}

module.exports = (state = initialRecordsState, action) => {
  switch (action.type) {
    case actionTypes.SET_CURRENT_RECORD: {
      const { recordIndex, record } = action
      return setCurrentRecord(state, recordIndex, record)
    }

    case actionTypes.NEW_RECORD: {
      return Object.assign({}, state, {
        newRecordIndex: action.atIndex
      })
    }

    case actionTypes.NEW_RECORD_RESULT: {
      return matchAction(action, {
        Error: () => {
          return Object.assign({}, state, {
            newRecordIndex: null
          })
        },
        Ok: record => {
          return Object.assign(
            {},
            setCurrentRecord(state, state.newRecordIndex, record),
            {
              newRecordIndex: null
            }
          )
        }
      })
    }

    case actionTypes.UPDATE_FIELDS: {
      const { fieldsToUpdate, updateSource } = action

      return Object.assign({}, state, {
        currentRecord: Object.assign({}, state.currentRecord, {
          fieldsToUpdate,
          updateSource
        })
      })
    }

    case actionTypes.GO_TO_INDEX: {
      return Object.assign({}, state, {
        desiredIndex: action.index,
        refreshCurrentRecord: true
      })
    }

    case actionTypes.GET_RECORD_BY_INDEX_RESULT: {
      return matchAction(action, {
        Error: () => {
          return Object.assign({}, state, {
            desiredIndex: state.currentRecord.index,
            refreshCurrentRecord: false
          })
        },
        Ok: ({ index, record }) => {
          return setCurrentRecord(state, index, record)
        }
      })
    }

    case actionTypes.REFRESH_CURRENT_RECORD: {
      return Object.assign({}, state, { refreshCurrentRecord: true })
    }

    case actionTypes.REMOVE_CURRENT_RECORD: {
      return Object.assign({}, state, { removeCurrentRecord: true })
    }

    case actionTypes.REMOVE_CURRENT_RECORD_RESULT: {
      return matchAction(action, {
        Error: () => {
          return Object.assign({}, state, {
            removeCurrentRecord: false
          })
        },
        Ok: () => {
          return Object.assign({}, state, {
            removeCurrentRecord: false,
            refreshCurrentRecord: true
          })
        }
      })
    }

    case actionTypes.REFRESH_CURRENT_VIEW: {
      return Object.assign({}, state, { refreshCurrentView: true })
    }

    case actionTypes.CURRENT_VIEW_UPDATED: {
      return Object.assign({}, state, { refreshCurrentView: false })
    }

    case actionTypes.REVERT_CHANGES: {
      return Object.assign({}, state, { revertChanges: true })
    }

    case actionTypes.RECORD_REVERTED: {
      return Object.assign({}, state, { revertChanges: false })
    }

    case actionTypes.SET_DEFAULT_RECORD: {
      const { record } = action

      return Object.assign({}, state, { defaultRecord: record })
    }

    case rootActions.actionTypes.SET_CONTROLLER_CONFIG: {
      const { pageSize } = action.config
      return Object.assign({}, state, {
        page: Object.assign({}, state.page, {
          size: pageSize || state.page.size
        })
      })
    }

    case rootActions.actionTypes.SET_PAGINATION_DATA: {
      const newState = Object.assign({}, state, {
        page: Object.assign({}, state.page, action.paginationData)
      })

      return isCurrentIndexInView(newState)
        ? newState
        : Object.assign({}, newState, {
            desiredIndex: newState.page.offset,
            refreshCurrentRecord: true
          })
    }

    case actionTypes.GO_TO_NEXT_PAGE: {
      const paginationData = getPaginationData(state)
      const currentPageSize = getCurrentPageSize(state)
      const offset = paginationData.offset + currentPageSize

      const newState = Object.assign({}, state, {
        page: Object.assign({}, state.page, {
          numPagesToShow: 1,
          offset
        })
      })

      return isCurrentIndexInView(newState)
        ? newState
        : Object.assign({}, newState, {
            desiredIndex: newState.page.offset,
            refreshCurrentRecord: true
          })
    }

    case actionTypes.GO_TO_PREVIOUS_PAGE: {
      const paginationData = getPaginationData(state)
      const offset = Math.max(0, paginationData.offset - paginationData.size)

      const newState = Object.assign({}, state, {
        page: Object.assign({}, state.page, {
          numPagesToShow: 1,
          offset
        })
      })

      return isCurrentIndexInView(newState)
        ? newState
        : Object.assign({}, newState, {
            desiredIndex: newState.page.offset,
            refreshCurrentRecord: true
          })
    }

    case actionTypes.LOAD_PAGE: {
      const { size } = getPaginationData(state)
      const newState = Object.assign({}, state, {
        page: Object.assign({}, state.page, {
          numPagesToShow: 1,
          offset: size * (action.pageNumber - 1)
        })
      })

      return isCurrentIndexInView(newState)
        ? newState
        : Object.assign({}, newState, {
            desiredIndex: newState.page.offset,
            refreshCurrentRecord: true
          })
    }

    case actionTypes.INCREMENT_NUM_PAGES_TO_SHOW: {
      return Object.assign({}, state, {
        page: Object.assign({}, state.page, {
          numPagesToShow: state.page.numPagesToShow + 1
        })
      })
    }

    case actionTypes.SAVE_RECORD: {
      if (isNone(state.saveState) || isError(state.saveState)) {
        return Object.assign({}, state, {
          saveState: saveStates.SAVE_STATE_CALL_BEFORE_SAVE_HOOKS,
          lastSavedRecord: {
            index: undefined,
            data: undefined
          }
        })
      }
      return state
    }

    case actionTypes.CALL_BEFORE_SAVE_HOOKS_RESULT: {
      return matchAction(action, {
        Error: () =>
          Object.assign({}, state, { saveState: saveStates.SAVE_STATE_NONE }),
        Ok: () =>
          Object.assign({}, state, {
            saveState: saveStates.SAVE_STATE_CHECK_HAS_DRAFT
          })
      })
    }

    case actionTypes.CHECK_HAS_DRAFT_RESULT: {
      return matchAction(action, {
        Error: () =>
          Object.assign({}, state, { saveState: saveStates.SAVE_STATE_ERROR }),
        Ok: hasDraft =>
          Object.assign({}, state, {
            saveState: hasDraft
              ? saveStates.SAVE_STATE_ASSERT_VALIDITY
              : saveStates.SAVE_STATE_NONE
          })
      })
    }

    case actionTypes.ASSERT_VALIDITY_RESULT: {
      return matchAction(action, {
        Error: () =>
          Object.assign({}, state, { saveState: saveStates.SAVE_STATE_NONE }),
        Ok: () =>
          Object.assign({}, state, {
            saveState: saveStates.SAVE_STATE_SAVE_RECORD
          })
      })
    }

    case actionTypes.SAVE_RECORD_RESULT: {
      return matchAction(action, {
        Error: () =>
          Object.assign({}, state, { saveState: saveStates.SAVE_STATE_ERROR }),
        Ok: savedRecord =>
          Object.assign({}, state, {
            saveState: saveStates.SAVE_STATE_NONE,
            lastSavedRecord: {
              index: state.currentRecord.index,
              data: savedRecord
            }
          })
      })
    }

    default:
      return state
  }
}

const getCurrentPageSize = state => state.page.size
const getNumberOfPagesToShow = state => state.page.numPagesToShow
const getPaginationData = state => state.page
const getCurrentPage = state =>
  state.page.offset / getCurrentPageSize(state) + getNumberOfPagesToShow(state)
const hasCurrentRecord = state => !!selectCurrentRecord(state)
const selectCurrentRecord = state => state.currentRecord.data
const selectCurrentRecordIndex = state => state.currentRecord.index
const selectDesiredRecordIndex = state => state.desiredIndex
const selectFieldsToUpdate = state => state.currentRecord.fieldsToUpdate
const selectLastSavedRecord = state => state.lastSavedRecord.data
const selectLastSavedRecordIndex = state => state.lastSavedRecord.index
const selectRefreshCurrentRecord = state => state.refreshCurrentRecord
const selectRemoveCurrentRecord = state => state.removeCurrentRecord
const selectRefreshCurrentView = state => state.refreshCurrentView
const selectRevertChanges = state => state.revertChanges
const selectSaveState = state => state.saveState
const selectUpdateSource = state => state.currentRecord.updateSource
const selectNewRecordIndex = state => state.newRecordIndex
module.exports.getCurrentPageSize = getCurrentPageSize
module.exports.getNumberOfPagesToShow = getNumberOfPagesToShow
module.exports.getPaginationData = getPaginationData
module.exports.getCurrentPage = getCurrentPage
module.exports.hasCurrentRecord = hasCurrentRecord
module.exports.selectCurrentRecord = selectCurrentRecord
module.exports.selectCurrentRecordIndex = selectCurrentRecordIndex
module.exports.selectDefaultDraft = getDefaultDraft
module.exports.selectDesiredRecordIndex = selectDesiredRecordIndex
module.exports.selectFieldsToUpdate = selectFieldsToUpdate
module.exports.selectLastSavedRecord = selectLastSavedRecord
module.exports.selectLastSavedRecordIndex = selectLastSavedRecordIndex
module.exports.selectRefreshCurrentRecord = selectRefreshCurrentRecord
module.exports.selectRemoveCurrentRecord = selectRemoveCurrentRecord
module.exports.selectRefreshCurrentView = selectRefreshCurrentView
module.exports.selectRevertChanges = selectRevertChanges
module.exports.selectSaveState = selectSaveState
module.exports.selectUpdateSource = selectUpdateSource
module.exports.selectNewRecordIndex = selectNewRecordIndex



// WEBPACK FOOTER //
// ./records/reducer.js